import { Head, Link, usePage } from '@inertiajs/react';
import { BlogLayout } from './BlogLayout';

// Types
interface Post {
  id: number;
  title: string;
  slug: string;
  summary: string;
  content: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number;
  isPremium: boolean;
  viewCount: number;
  author: {
    id: number;
    name: string;
    avatarUrl: string | null;
  } | null;
  categories: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
  tags: Array<{
    id: number;
    name: string;
    slug: string;
  }>;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  postCount: number;
}

interface Tag {
  id: number;
  name: string;
  slug: string;
  postCount: number;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  perPage: number;
  nextPage: number | null;
  prevPage: number | null;
}

interface SEO {
  title: string;
  description: string;
  og_title: string;
  og_description: string;
  og_type: string;
  twitter_card: string;
}

interface PageProps {
  query: string;
  posts: Post[];
  popularPosts: Post[];
  categories: Category[];
  tags: Tag[];
  pagination: Pagination;
  seo: SEO;
}

// Helper function to format date
function formatDate(dateString: string | null): string {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Highlight search terms in text
function highlightText(text: string, query: string): string {
  if (!query || !text) return text;
  
  const regex = new RegExp(`(${escapeRegExp(query)})`, 'gi');
  return text.replace(regex, '<mark class="bg-yellow-200 dark:bg-yellow-800 px-0.5 rounded text-foreground">$1</mark>');
}

// Escape special regex characters
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Premium Badge Component
function PremiumBadge() {
  return (
    <span className="inline-flex items-center gap-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium px-2 py-1 rounded-full">
      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      Premium
    </span>
  );
}

// Post Card Component
function SearchResultCard({ post, query }: { post: Post; query: string }) {
  return (
    <article className="bg-card dark:bg-card text-foreground rounded-lg border border-border overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-3">
          {post.isPremium && <PremiumBadge />}
          {post.categories.length > 0 && (
            post.categories.slice(0, 1).map((category) => (
              <Link
                key={category.id}
                href={`/blog/category/${category.slug}`}
                className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full hover:bg-primary/20 transition-colors"
              >
                {category.name}
              </Link>
            ))
          )}
        </div>
        <Link href={`/blog/${post.slug}`}>
          <h3 
            className="text-lg font-semibold text-foreground mb-2 hover:text-primary transition-colors"
            dangerouslySetInnerHTML={{ __html: highlightText(post.title, query) }}
          />
        </Link>
        <p 
          className="text-muted-foreground text-sm mb-4 line-clamp-2"
          dangerouslySetInnerHTML={{ __html: highlightText(post.summary, query) }}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center space-x-2">
            {post.author && (
              <>
                <span>{post.author.name}</span>
                <span>•</span>
              </>
            )}
            <span>{formatDate(post.publishedAt)}</span>
          </div>
          <span>{post.readingTimeMinutes} min</span>
        </div>
      </div>
    </article>
  );
}

// Popular Post Card
function PopularPostCard({ post }: { post: Post }) {
  return (
    <article className="py-3 border-b border-border last:border-0">
      <Link href={`/blog/${post.slug}`} className="group">
        <h4 className="text-sm font-medium text-foreground group-hover:text-primary transition-colors line-clamp-2">
          {post.title}
        </h4>
        <p className="text-xs text-muted-foreground mt-1">
          {formatDate(post.publishedAt)} • {post.viewCount} views
        </p>
      </Link>
    </article>
  );
}

// Pagination Component
function Pagination({ pagination, query }: { pagination: Pagination; query: string }) {
  const { currentPage, totalPages, prevPage, nextPage } = pagination;
  
  if (totalPages <= 1) return null;

  return (
    <div className="flex justify-center items-center space-x-2 mt-12">
      {prevPage ? (
        <Link
          href={`/blog/search?q=${encodeURIComponent(query)}&page=${prevPage}`}
          className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
        >
          Previous
        </Link>
      ) : (
        <span className="px-4 py-2 border border-border rounded-lg text-muted-foreground cursor-not-allowed">
          Previous
        </span>
      )}
      
      <div className="flex items-center space-x-1">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <Link
            key={page}
            href={`/blog/search?q=${encodeURIComponent(query)}&page=${page}`}
            className={`w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              page === currentPage
                ? 'bg-primary text-primary-foreground'
                : 'text-foreground hover:bg-muted'
            }`}
          >
            {page}
          </Link>
        ))}
      </div>
      
      {nextPage ? (
        <Link
          href={`/blog/search?q=${encodeURIComponent(query)}&page=${nextPage}`}
          className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-muted transition-colors"
        >
          Next
        </Link>
      ) : (
        <span className="px-4 py-2 border border-border rounded-lg text-muted-foreground cursor-not-allowed">
          Next
        </span>
      )}
    </div>
  );
}

// Search Input Component
function SearchInput({ defaultValue }: { defaultValue: string }) {
  return (
    <form action="/blog/search" method="GET" className="relative">
      <input
        type="text"
        name="q"
        defaultValue={defaultValue}
        placeholder="Search blog posts..."
        className="w-full px-5 py-3 pr-12 border border-input rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-shadow bg-background text-foreground"
        autoFocus
      />
      <button
        type="submit"
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-muted-foreground hover:text-primary transition-colors"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </button>
    </form>
  );
}

// Main Search Page
export default function BlogSearch() {
  const { query, posts, popularPosts, categories, tags, pagination, seo } = usePage<PageProps>().props;

  return (
    <BlogLayout>
      <Head>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta property="og:title" content={seo.og_title} />
        <meta property="og:description" content={seo.og_description} />
        <meta property="og:type" content={seo.og_type} />
        <meta name="twitter:card" content={seo.twitter_card} />
      </Head>

      <div className="space-y-8">
        {/* Search Form */}
        <section className="bg-card dark:bg-card text-foreground rounded-lg border border-border p-6">
          <h1 className="text-2xl font-bold text-foreground mb-4">Search</h1>
          <SearchInput defaultValue={query} />
          {query && (
            <p className="text-sm text-muted-foreground mt-3">
              {pagination.totalCount} result{pagination.totalCount !== 1 ? 's' : ''} for "{query}"
            </p>
          )}
        </section>

        {/* Search Results */}
        <section>
          {query ? (
            posts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {posts.map((post) => (
                  <SearchResultCard key={post.id} post={post} query={query} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-card dark:bg-card text-foreground rounded-lg border border-border">
                <svg className="w-16 h-16 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <h3 className="text-lg font-medium text-foreground mb-2">No results found</h3>
                <p className="text-muted-foreground mb-4">
                  Try different keywords or browse our popular posts.
                </p>
                <Link
                  href="/blog"
                  className="inline-flex items-center text-primary hover:text-primary/80 font-medium"
                >
                  Browse all posts
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>
            )
          ) : (
            <div className="text-center py-12 bg-card dark:bg-card text-foreground rounded-lg border border-border">
              <svg className="w-16 h-16 mx-auto text-muted-foreground mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="text-lg font-medium text-foreground mb-2">Search our blog</h3>
              <p className="text-muted-foreground">
                Enter a keyword to find posts.
              </p>
            </div>
          )}
        </section>

        {/* Pagination */}
        {query && posts.length > 0 && (
          <Pagination pagination={pagination} query={query} />
        )}

        {/* Popular Posts - shown when no search query or results */}
        {!query && popularPosts.length > 0 && (
          <section className="bg-card dark:bg-card text-foreground rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Popular Posts</h2>
            <div className="divide-y divide-border">
              {popularPosts.map((post) => (
                <PopularPostCard key={post.id} post={post} />
              ))}
            </div>
          </section>
        )}
      </div>
    </BlogLayout>
  );
}
